import type { NetMessage } from './transport';
import type {
  ITile,
  IRoom,
  PlacedLocation,
  MapStyle,
} from '../types/map.types';
import type { SpellState } from '../types/spell.types';
import type { ActiveBuff } from '../types/buff.types';
import type { MonsterMapIcon } from '../data/monsters.config';
import { DungeonEntityType } from '../types/enums';
import { dungeonService } from '../services/DungeonService';

/** A host buff replicated for client display (icon, size/opacity via recompute,
 *  HUD duration/stacks). The static def is looked up from `BUFF_REGISTRY[id]`
 *  client-side — only the live id/duration/stacks transit. */
export interface BuffSnapshot {
  id: string;
  /** Remaining ticks (omitted for permanent buffs). */
  remainingTicks?: number;
  /** Stack count (omitted when 1). */
  stacks?: number;
}

export interface EntitySnapshot {
  id: string;
  type: string;
  configId: string;
  // Logical cell (positionX/Y) — used to reconstruct the entity.
  x: number;
  y: number;
  // Smoothed visual position in world px (absoluteX/Y) — interpolated on the
  // client. The logical cell is a step function (it jumps mid-move), so
  // interpolating it would teleport; the visual position moves continuously.
  ax: number;
  ay: number;
  direction: number;
  life: number;
  maxLife: number;
  sprite: string;
  // Last input sequence the host has processed for this player (client
  // prediction reconciliation). 0 for non-player / unprocessed entities.
  ackSeq: number;
  // Non-zero spell cooldowns — players only, for the client HUD (predicted
  // locally on cast, reconciled from this). Absent/empty = all ready.
  cooldowns?: SpellState[];
  // Non-internal active buffs, for client display (elite aura, debuff icons,
  // size/opacity). Absent/empty = no buffs — the client then clears any it held
  // (a host-side removal, e.g. antidote, drops the buff from the snapshot).
  buffs?: BuffSnapshot[];
  // Map/minimap marker (monsters). Set post-`createMonster` on the host (elite /
  // miniboss / boss), so the client — which rebuilds from `configId` — would
  // otherwise miss it. Omitted when 'normal'.
  mapIcon?: MonsterMapIcon;
}

export interface SnapshotMessage extends NetMessage {
  type: 'snapshot';
  t: number;
  seq: number;
  entities: EntitySnapshot[];
}

export interface FloorMessage extends NetMessage {
  type: 'floor';
  tiles: ITile[];
  width: number;
  height: number;
  tilesetKey: string;
  biomeId: string;
  localPlayerId: string;
  placedLocations: PlacedLocation[];
  rooms: IRoom[];
  style: MapStyle;
  floor?: number;
  commonItems?: {
    id: string;
    itemId: string;
    x: number;
    y: number;
    quantity: number;
    dropperId?: string;
  }[];
  // Location consumption snapshot, so a (re)joining client restores its own
  // prompts: global once-per-spawn `used`/`destroyed`, plus the full per-player
  // `usedByPlayer` map (each client reads only its own id via `isConsumedFor`).
  usedLocations?: string[];
  destroyedLocations?: string[];
  usedByPlayer?: Record<string, string[]>;
}

interface SnapshotSource {
  id: string;
  type: string;
  configId: string;
  positionX: number;
  positionY: number;
  absoluteX: number;
  absoluteY: number;
  direction: number;
  life: number;
  stats: { maxLife: number };
  currentSpriteName: string;
  lastInputSeq: number;
  mapIcon?: MonsterMapIcon;
  getSpellStatesSnapshot?: () => SpellState[];
  getActiveBuffs?: () => ActiveBuff[];
}

function replicatedBuffs(e: SnapshotSource): BuffSnapshot[] | undefined {
  const active = e.getActiveBuffs?.();
  if (!active || active.length === 0) {
    return undefined;
  }
  const out: BuffSnapshot[] = [];
  for (const b of active) {
    // `internal` buffs carry host-only passives (trinkets/talents) — no icon, no
    // client-visible effect; never replicated.
    if (b.def.internal) {
      continue;
    }
    const snap: BuffSnapshot = { id: b.def.id };
    if (b.remainingTicks !== undefined) {
      snap.remainingTicks = b.remainingTicks;
    }
    if (b.stacks !== undefined && b.stacks > 1) {
      snap.stacks = b.stacks;
    }
    out.push(snap);
  }
  return out.length > 0 ? out : undefined;
}

export function entityToSnapshot(e: SnapshotSource): EntitySnapshot {
  const snap: EntitySnapshot = {
    id: e.id,
    type: e.type,
    configId: e.configId,
    x: e.positionX,
    y: e.positionY,
    ax: e.absoluteX,
    ay: e.absoluteY,
    direction: e.direction,
    life: e.life,
    maxLife: e.stats.maxLife,
    sprite: e.currentSpriteName,
    ackSeq: e.lastInputSeq,
  };
  if (e.type === DungeonEntityType.PLAYER && e.getSpellStatesSnapshot) {
    snap.cooldowns = e.getSpellStatesSnapshot();
  }
  const buffs = replicatedBuffs(e);
  if (buffs) {
    snap.buffs = buffs;
  }
  if (e.mapIcon && e.mapIcon !== 'normal') {
    snap.mapIcon = e.mapIcon;
  }
  return snap;
}

export function serializeSnapshot(t: number, seq: number): SnapshotMessage {
  const entities = dungeonService
    .getEntities()
    .map((e) => entityToSnapshot(e as unknown as SnapshotSource));
  return { type: 'snapshot', t, seq, entities };
}

export function serializeFloor(
  tiles: ITile[],
  width: number,
  height: number,
  tilesetKey: string,
  biomeId: string,
  localPlayerId: string,
  placedLocations: PlacedLocation[],
  rooms: IRoom[],
  style: MapStyle,
): FloorMessage {
  return {
    type: 'floor',
    tiles,
    width,
    height,
    tilesetKey,
    biomeId,
    localPlayerId,
    placedLocations,
    rooms,
    style,
  };
}
